import { createActionGroup, emptyProps, props } from "@ngrx/store";
import { BaseCommentFragment, BaseCommentModeFragment, CreateCommentMutation, QuillTemplate, UpdateCommentsMutation } from "graphql.generated";
import { Delta } from "quill";

import { ActionSource } from "../../state/action-sources";

import { EditableComment, PendingFile } from "./job-activity.state";

interface CommentIdentifiers {
  commentId: string;
  parentId?: string;
}

export const JobActivityActions = createActionGroup({
  source: ActionSource.ACTIVITY,
  events: {
    'Add Comment Contents Changed': props<{ contents: Delta }>(),
    'Add Comment Mode Selected': props<{ commentModeId: string }>(),
    'Add Comment Mode Set': props<BaseCommentModeFragment>(),
    'Add Comment Subject Changed': props<{ subject: Delta }>(),
    'Add Comment Template Selected': props<{ template: QuillTemplate }>(),
    'Add Comment Template Subject Set': props<{ subject: Delta }>(),
    'Add Comment Files Selected': props<{ files: PendingFile[] }>(),
    'Add Comment Files Rejected': props<{ rejected: { name: string; reason: string }[] }>(),
    'Add Comment Files Attached': props<{ files: PendingFile[] }>(),
    'Add Comment Files Discarded': props<{ files: { fileId: string; name: string; reason: 'duplicate' | 'total-size' }[] }>(),
    'Add Comment File Removed': props<{ fileId: string }>(),
    'Add Comment Button Clicked': emptyProps(),
    'Add Comment Ctrl Enter Pressed': emptyProps(),
    'New Comment Added': props<{ comment: EditableComment; pendingFileIds: string[] }>(),
    'Add Comment Attachments Uploaded': props<{ commentId: string; artifactIds: string[]; fileIds: string[] }>(),
    'Add Comment Attachments Upload Error': props<{ commentId: string; error: Error }>(),
    'Create Comment Success': props<{
      jobId: string;
      newComment: CreateCommentMutation['addComment'];
      changeIds: string[];
    }>(),
    'Create Comment Error': props<{ error: Error }>(),
    'Update Comment Success': props<{
      parentId: string;
      commentId: string;
      jobId: string;
      comment: UpdateCommentsMutation['updateComments'][number];
    }>(),
    'Update Comment Error': props<CommentIdentifiers & { error: Error }>(),
    'Edit Comment Button Clicked': props<CommentIdentifiers>(),
    'Save Comment Edits Button Clicked': props<CommentIdentifiers>(),
    'Cancel Comment Edits Button Clicked': props<{ commentId: string }>(),
    'Comment Edit Content Changed': props<{ commentId: string; content: Delta }>(),
    'Comment Edit Enter Key Pressed': props<CommentIdentifiers>(),
    'Reply Button Clicked': props<CommentIdentifiers>(),
    'Reply Input Focused': props<{ commentId: string; }>(),
    'Reply Input Content Changed': props<{ commentId: string; content: Delta }>(),
    'Reply Input Enter Key Pressed': props<CommentIdentifiers>(),
    'Add Reply Button Clicked': props<CommentIdentifiers>(),
    'Reply Files Selected': props<{ commentId: string; files: PendingFile[] }>(),
    'Reply Files Rejected': props<{ rejected: { name: string; reason: string }[] }>(),
    'Reply Files Attached': props<{ commentId: string; files: PendingFile[] }>(),
    'Reply Files Discarded': props<{ commentId: string; files: { fileId: string; name: string; reason: 'duplicate' | 'total-size' }[] }>(),
    'Reply File Removed': props<{ commentId: string; fileId: string }>(),
    'Reply Attachments Uploaded': props<{ commentId: string; parentId: string; artifactIds: string[]; fileIds: string[] }>(),
    'Reply Attachments Upload Error': props<{ commentId: string; parentId: string; error: Error }>(),
    'Reply Added': props<CommentIdentifiers & { comment: EditableComment; pendingFileIds?: string[] }>(),
    'Cancel Reply Button Clicked': props<{ commentId: string; }>(),
    'Delete Comment Button Clicked': props<CommentIdentifiers>(),
    'Delete Comment Menu Item Clicked': props<CommentIdentifiers>(),
    'Delete Comment Success': props<CommentIdentifiers & { jobId: string; }>(),
    'Delete Comment Error': props<CommentIdentifiers & { error: Error }>(),
    'Confirm Delete Comment Button Clicked': props<CommentIdentifiers>(),
    'Author Name Clicked': props<{ userId: string }>(),
    'Quill Editor Time Clicked': props<{ time: string }>(),
    'Toggle Expand Replies Button Clicked': props<{ commentId: string; }>(),
    'File Chip Clicked': props<{ artifact: BaseCommentFragment['artifacts'][number] }>(),

    'Update Heartbeat Requested': props<{
        jobId: string;
        nextHeartbeat?: number;
        aiDisabled?: number;
    }>(),
    'Update Heartbeat Success': emptyProps(),
    'Update Heartbeat Error': props<{ error: { message: string } }>(),
  },
});
